import { MenuItem, MenuCategory, Order, TableInfo, CafeInfo, Organization } from '../types';

/**
 * Demo Data Configuration
 * 
 * This file contains all demo data for the BeanBuzz demo organization.
 * It serves as a single source of truth for demo content and provides
 * realistic sample data for testing and demonstration purposes.
 * 
 * @fileoverview Centralized demo data for BeanBuzz demo organization
 * @author BeanBuzz Team
 * @version 1.0.0
 */

/**
 * Demo organization URL name used for routing and identification
 * This is the unique identifier for the demo organization
 */
export const DEMO_ORG_URL_NAME = 'beanbuzz-demo';

/**
 * Demo organization configuration
 * Contains the basic organization information for the demo
 */
export const DEMO_ORGANIZATION: Organization = {
  id: 1,
  name: 'BeanBuzz Demo',
  urlName: DEMO_ORG_URL_NAME,
  createdBy: 'demo@beanbuzz.com',
  createdTime: new Date().toISOString(),
  deleted: false
};

/**
 * Demo cafe information and business details
 * Contains realistic cafe information for demonstration purposes
 */
export const DEMO_CAFE_INFO: CafeInfo = {
  name: 'BeanBuzz Demo Cafe',
  description: 'Welcome to our demo cafe! Experience the full BeanBuzz platform with sample menu items and ordering features. This is a demonstration of our comprehensive cafe management system.',
  hours: {
    monday: '7:00 AM - 9:00 PM',
    tuesday: '7:00 AM - 9:00 PM',
    wednesday: '7:00 AM - 9:00 PM',
    thursday: '7:00 AM - 9:00 PM',
    friday: '7:00 AM - 10:00 PM',
    saturday: '8:00 AM - 10:00 PM',
    sunday: '8:00 AM - 8:00 PM'
  },
  location: '123 Demo Street, BeanBuzz City, BC 12345',
  phone: '(555) 123-4567',
  email: 'demo@beanbuzz.com',
  website: 'https://beanbuzz.com/demo'
};



// Demo Menu Categories
export const DEMO_MENU_CATEGORIES: MenuCategory[] = [
  {
    id: 1,
    name: 'Hot Beverages',
    description: 'Freshly brewed hot drinks and specialty coffee',
    displayOrder: 1,
    isActive: true,
    orgId: 1,
    createdTime: new Date().toISOString(),
    updatedTime: new Date().toISOString()
  },
  {
    id: 2,
    name: 'Cold Beverages',
    description: 'Refreshing cold drinks and iced specialties',
    displayOrder: 2,
    isActive: true,
    orgId: 1,
    createdTime: new Date().toISOString(),
    updatedTime: new Date().toISOString()
  },
  {
    id: 3,
    name: 'Pastries & Snacks',
    description: 'Fresh pastries, baked goods, and light snacks',
    displayOrder: 3,
    isActive: true,
    orgId: 1,
    createdTime: new Date().toISOString(),
    updatedTime: new Date().toISOString()
  },
  {
    id: 4,
    name: 'Main Dishes',
    description: 'Heartier meals and lunch options',
    displayOrder: 4,
    isActive: true,
    orgId: 1,
    createdTime: new Date().toISOString(),
    updatedTime: new Date().toISOString()
  },
  {
    id: 5,
    name: 'Desserts',
    description: 'Sweet treats and indulgent desserts',
    displayOrder: 5,
    isActive: true,
    orgId: 1,
    createdTime: new Date().toISOString(),
    updatedTime: new Date().toISOString()
  },
  {
    id: 6,
    name: 'Healthy Options',
    description: 'Nutritious and wholesome choices',
    displayOrder: 6,
    isActive: true,
    orgId: 1,
    createdTime: new Date().toISOString(),
    updatedTime: new Date().toISOString()
  }
];

// Demo Menu Items
export const DEMO_MENU_ITEMS: MenuItem[] = [
  // Hot Beverages
  {
    id: 1,
    name: 'Espresso',
    description: 'Rich and bold single-origin espresso shot',
    price: 2.50,
    currency: 'USD',
    categoryId: 1,
    isAvailable: true,
    isFeatured: true,
    displayOrder: 1,
    imageUrl: 'https://images.unsplash.com/photo-1509042239860-f550ce710b93?w=400',
    orgId: 1,
    preparationTimeMinutes: 2,
    allergens: [],
    ingredients: ['espresso beans', 'water'],
    nutritionalInfo: { calories: 5, protein: 0, carbs: 1, fat: 0 },
    customizationOptions: { sizes: ['single', 'double'], extras: ['sugar', 'cream'] },
    createdTime: new Date().toISOString(),
    updatedTime: new Date().toISOString()
  },
  {
    id: 2,
    name: 'Cappuccino',
    description: 'Espresso with steamed milk and velvety foam',
    price: 4.25,
    currency: 'USD',
    categoryId: 1,
    isAvailable: true,
    isFeatured: true,
    displayOrder: 2,
    imageUrl: 'https://images.unsplash.com/photo-1572445271230-a78b5944a659?w=400',
    orgId: 1,
    preparationTimeMinutes: 4,
    allergens: ['dairy'],
    ingredients: ['espresso', 'steamed milk', 'milk foam'],
    nutritionalInfo: { calories: 80, protein: 4, carbs: 6, fat: 4 },
    customizationOptions: { milk: ['whole', 'skim', 'oat', 'almond'], extras: ['extra foam', 'cinnamon'] },
    createdTime: new Date().toISOString(),
    updatedTime: new Date().toISOString()
  },
  {
    id: 3,
    name: 'Latte',
    description: 'Smooth espresso with steamed milk and light foam',
    price: 4.75,
    currency: 'USD',
    categoryId: 1,
    isAvailable: true,
    isFeatured: false,
    displayOrder: 3,
    imageUrl: 'https://images.unsplash.com/photo-1544787219-7f47ccb76574?w=400',
    orgId: 1,
    preparationTimeMinutes: 4,
    allergens: ['dairy'],
    ingredients: ['espresso', 'steamed milk', 'light foam'],
    nutritionalInfo: { calories: 120, protein: 6, carbs: 8, fat: 6 },
    customizationOptions: { milk: ['whole', 'skim', 'oat', 'almond'], flavors: ['vanilla', 'caramel', 'hazelnut'] },
    createdTime: new Date().toISOString(),
    updatedTime: new Date().toISOString()
  },
  {
    id: 4,
    name: 'Americano',
    description: 'Espresso with hot water for a smooth, rich taste',
    price: 3.25,
    currency: 'USD',
    categoryId: 1,
    isAvailable: true,
    isFeatured: false,
    displayOrder: 4,
    imageUrl: 'https://images.unsplash.com/photo-1511920170033-f8396924c348?w=400',
    orgId: 1,
    preparationTimeMinutes: 3,
    allergens: [],
    ingredients: ['espresso', 'hot water'],
    nutritionalInfo: { calories: 10, protein: 0, carbs: 2, fat: 0 },
    customizationOptions: { sizes: ['small', 'medium', 'large'], extras: ['cream', 'sugar'] },
    createdTime: new Date().toISOString(),
    updatedTime: new Date().toISOString()
  },
  {
    id: 5,
    name: 'Chai Latte',
    description: 'Spiced black tea with steamed milk and honey',
    price: 4.50,
    currency: 'USD',
    categoryId: 1,
    isAvailable: true,
    isFeatured: false,
    displayOrder: 5,
    imageUrl: 'https://images.unsplash.com/photo-1571934811356-5cc061b6821f?w=400',
    orgId: 1,
    preparationTimeMinutes: 5,
    allergens: ['dairy'],
    ingredients: ['black tea', 'spices', 'steamed milk', 'honey'],
    nutritionalInfo: { calories: 140, protein: 4, carbs: 20, fat: 4 },
    customizationOptions: { milk: ['whole', 'oat', 'almond'], sweetness: ['regular', 'less sweet', 'extra sweet'] },
    createdTime: new Date().toISOString(),
    updatedTime: new Date().toISOString()
  },

  // Cold Beverages
  {
    id: 6,
    name: 'Iced Coffee',
    description: 'Cold brew coffee served over ice',
    price: 3.50,
    currency: 'USD',
    categoryId: 2,
    isAvailable: true,
    isFeatured: false,
    displayOrder: 1,
    imageUrl: 'https://images.unsplash.com/photo-1461023058943-07fcbe16d735?w=400',
    orgId: 1,
    preparationTimeMinutes: 2,
    allergens: [],
    ingredients: ['cold brew coffee', 'ice'],
    nutritionalInfo: { calories: 5, protein: 0, carbs: 1, fat: 0 },
    customizationOptions: { sizes: ['medium', 'large'], extras: ['milk', 'sugar', 'cream'] },
    createdTime: new Date().toISOString(),
    updatedTime: new Date().toISOString()
  },
  {
    id: 7,
    name: 'Frappuccino',
    description: 'Blended iced coffee with whipped cream and caramel drizzle',
    price: 5.25,
    currency: 'USD',
    categoryId: 2,
    isAvailable: true,
    isFeatured: true,
    displayOrder: 2,
    imageUrl: 'https://images.unsplash.com/photo-1572445271230-a78b5944a659?w=400',
    orgId: 1,
    preparationTimeMinutes: 6,
    allergens: ['dairy'],
    ingredients: ['coffee', 'milk', 'ice', 'whipped cream', 'caramel'],
    nutritionalInfo: { calories: 280, protein: 6, carbs: 35, fat: 12 },
    customizationOptions: { flavors: ['caramel', 'mocha', 'vanilla'], extras: ['extra whip', 'extra caramel'] },
    createdTime: new Date().toISOString(),
    updatedTime: new Date().toISOString()
  },
  {
    id: 8,
    name: 'Fresh Juice',
    description: 'Daily selection of fresh fruit and vegetable juices',
    price: 5.99,
    currency: 'USD',
    categoryId: 2,
    isAvailable: true,
    isFeatured: false,
    displayOrder: 3,
    imageUrl: 'https://images.unsplash.com/photo-1621506289937-a8e4df240d0b?w=400',
    orgId: 1,
    preparationTimeMinutes: 3,
    allergens: [],
    ingredients: ['fresh fruits', 'vegetables'],
    nutritionalInfo: { calories: 140, protein: 2, carbs: 30, fat: 1 },
    customizationOptions: { types: ['orange', 'apple', 'carrot', 'beet', 'mixed'] },
    createdTime: new Date().toISOString(),
    updatedTime: new Date().toISOString()
  },

  // Pastries & Snacks
  {
    id: 9,
    name: 'Croissant',
    description: 'Buttery, flaky French pastry',
    price: 3.00,
    currency: 'USD',
    categoryId: 3,
    isAvailable: true,
    isFeatured: false,
    displayOrder: 1,
    imageUrl: 'https://images.unsplash.com/photo-1555507036-ab1f4038808a?w=400',
    orgId: 1,
    preparationTimeMinutes: 1,
    allergens: ['gluten', 'dairy', 'eggs'],
    ingredients: ['flour', 'butter', 'eggs', 'yeast'],
    nutritionalInfo: { calories: 260, protein: 6, carbs: 25, fat: 15 },
    customizationOptions: { types: ['plain', 'chocolate', 'almond'] },
    createdTime: new Date().toISOString(),
    updatedTime: new Date().toISOString()
  },
  {
    id: 10,
    name: 'Blueberry Muffin',
    description: 'Fresh baked muffin with plump blueberries',
    price: 2.75,
    currency: 'USD',
    categoryId: 3,
    isAvailable: true,
    isFeatured: false,
    displayOrder: 2,
    imageUrl: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400',
    orgId: 1,
    preparationTimeMinutes: 1,
    allergens: ['gluten', 'dairy', 'eggs'],
    ingredients: ['flour', 'blueberries', 'sugar', 'eggs', 'milk'],
    nutritionalInfo: { calories: 320, protein: 5, carbs: 45, fat: 12 },
    customizationOptions: { extras: ['butter', 'cream cheese'] },
    createdTime: new Date().toISOString(),
    updatedTime: new Date().toISOString()
  },
  {
    id: 11,
    name: 'Chocolate Chip Cookie',
    description: 'Warm, gooey chocolate chip cookie',
    price: 2.25,
    currency: 'USD',
    categoryId: 3,
    isAvailable: true,
    isFeatured: true,
    displayOrder: 3,
    imageUrl: 'https://images.unsplash.com/photo-1565958011703-44f9829ba187?w=400',
    orgId: 1,
    preparationTimeMinutes: 1,
    allergens: ['gluten', 'dairy', 'eggs'],
    ingredients: ['flour', 'chocolate chips', 'butter', 'eggs', 'sugar'],
    nutritionalInfo: { calories: 180, protein: 2, carbs: 22, fat: 9 },
    customizationOptions: { extras: ['ice cream', 'milk'] },
    createdTime: new Date().toISOString(),
    updatedTime: new Date().toISOString()
  },

  // Main Dishes
  {
    id: 12,
    name: 'Avocado Toast',
    description: 'Sourdough toast with smashed avocado, sea salt, and red pepper flakes',
    price: 8.50,
    currency: 'USD',
    categoryId: 4,
    isAvailable: true,
    isFeatured: true,
    displayOrder: 1,
    imageUrl: 'https://images.unsplash.com/photo-1541519227354-08fa5d50c44d?w=400',
    orgId: 1,
    preparationTimeMinutes: 8,
    allergens: ['gluten'],
    ingredients: ['sourdough bread', 'avocado', 'sea salt', 'red pepper flakes'],
    nutritionalInfo: { calories: 320, protein: 8, carbs: 25, fat: 22 },
    customizationOptions: { extras: ['eggs', 'bacon', 'cheese'], sides: ['fruit', 'salad'] },
    createdTime: new Date().toISOString(),
    updatedTime: new Date().toISOString()
  },
  {
    id: 13,
    name: 'Grilled Cheese',
    description: 'Classic grilled cheese with aged cheddar on artisan bread',
    price: 7.25,
    currency: 'USD',
    categoryId: 4,
    isAvailable: true,
    isFeatured: false,
    displayOrder: 2,
    imageUrl: 'https://images.unsplash.com/photo-1565299624946-b28f40a0ca4b?w=400',
    orgId: 1,
    preparationTimeMinutes: 6,
    allergens: ['gluten', 'dairy'],
    ingredients: ['artisan bread', 'aged cheddar', 'butter'],
    nutritionalInfo: { calories: 420, protein: 15, carbs: 30, fat: 28 },
    customizationOptions: { extras: ['tomato soup', 'bacon', 'onions'] },
    createdTime: new Date().toISOString(),
    updatedTime: new Date().toISOString()
  },
  {
    id: 14,
    name: 'Caesar Salad',
    description: 'Fresh romaine lettuce with Caesar dressing, croutons, and parmesan',
    price: 9.75,
    currency: 'USD',
    categoryId: 4,
    isAvailable: true,
    isFeatured: false,
    displayOrder: 3,
    imageUrl: 'https://images.unsplash.com/photo-1540420773420-3366772f4999?w=400',
    orgId: 1,
    preparationTimeMinutes: 5,
    allergens: ['gluten', 'dairy', 'eggs'],
    ingredients: ['romaine lettuce', 'Caesar dressing', 'croutons', 'parmesan'],
    nutritionalInfo: { calories: 280, protein: 8, carbs: 15, fat: 22 },
    customizationOptions: { protein: ['chicken', 'shrimp', 'salmon'], extras: ['extra dressing', 'extra cheese'] },
    createdTime: new Date().toISOString(),
    updatedTime: new Date().toISOString()
  },

  // Desserts
  {
    id: 15,
    name: 'Chocolate Lava Cake',
    description: 'Warm chocolate cake with molten center, served with vanilla ice cream',
    price: 8.99,
    currency: 'USD',
    categoryId: 5,
    isAvailable: true,
    isFeatured: true,
    displayOrder: 1,
    imageUrl: 'https://images.unsplash.com/photo-1563805042-7684c019e1cb?w=400',
    orgId: 1,
    preparationTimeMinutes: 12,
    allergens: ['gluten', 'dairy', 'eggs'],
    ingredients: ['chocolate', 'flour', 'eggs', 'butter', 'vanilla ice cream'],
    nutritionalInfo: { calories: 380, protein: 6, carbs: 45, fat: 20 },
    customizationOptions: { iceCream: ['vanilla', 'chocolate', 'strawberry'], extras: ['berries', 'whipped cream'] },
    createdTime: new Date().toISOString(),
    updatedTime: new Date().toISOString()
  },
  {
    id: 16,
    name: 'Tiramisu',
    description: 'Classic Italian dessert with espresso-soaked ladyfingers and mascarpone',
    price: 7.99,
    currency: 'USD',
    categoryId: 5,
    isAvailable: true,
    isFeatured: false,
    displayOrder: 2,
    imageUrl: 'https://images.unsplash.com/photo-1571877227200-a0d98ea607e9?w=400',
    orgId: 1,
    preparationTimeMinutes: 5,
    allergens: ['gluten', 'dairy', 'eggs'],
    ingredients: ['ladyfingers', 'mascarpone', 'espresso', 'cocoa powder'],
    nutritionalInfo: { calories: 320, protein: 8, carbs: 35, fat: 18 },
    customizationOptions: { coffee: ['regular', 'decaf'], extras: ['extra cocoa', 'berries'] },
    createdTime: new Date().toISOString(),
    updatedTime: new Date().toISOString()
  },

  // Healthy Options
  {
    id: 17,
    name: 'Acai Bowl',
    description: 'Acai smoothie bowl with granola, berries, and honey',
    price: 11.50,
    currency: 'USD',
    categoryId: 6,
    isAvailable: true,
    isFeatured: true,
    displayOrder: 1,
    imageUrl: 'https://images.unsplash.com/photo-1621506289937-a8e4df240d0b?w=400',
    orgId: 1,
    preparationTimeMinutes: 8,
    allergens: ['nuts'],
    ingredients: ['acai', 'granola', 'berries', 'honey', 'banana'],
    nutritionalInfo: { calories: 280, protein: 6, carbs: 45, fat: 8 },
    customizationOptions: { toppings: ['coconut', 'chia seeds', 'almonds'], extras: ['protein powder', 'peanut butter'] },
    createdTime: new Date().toISOString(),
    updatedTime: new Date().toISOString()
  },
  {
    id: 18,
    name: 'Quinoa Bowl',
    description: 'Quinoa with roasted vegetables, avocado, and tahini dressing',
    price: 12.75,
    currency: 'USD',
    categoryId: 6,
    isAvailable: true,
    isFeatured: false,
    displayOrder: 2,
    imageUrl: 'https://images.unsplash.com/photo-1565299624946-b28f40a0ca4b?w=400',
    orgId: 1,
    preparationTimeMinutes: 10,
    allergens: ['nuts'],
    ingredients: ['quinoa', 'roasted vegetables', 'avocado', 'tahini dressing'],
    nutritionalInfo: { calories: 420, protein: 12, carbs: 45, fat: 18 },
    customizationOptions: { protein: ['chicken', 'tofu', 'chickpeas'], extras: ['extra vegetables', 'extra dressing'] },
    createdTime: new Date().toISOString(),
    updatedTime: new Date().toISOString()
  }
];

// Demo Tables
export const DEMO_TABLES: TableInfo[] = [
  {
    id: '1',
    name: 'Table 1',
    description: 'Window seat with great view',
    displayOrder: 1,
    isActive: true,
    qrCodeUrl: 'https://via.placeholder.com/200x200/667eea/ffffff?text=Table+1',
    createdTime: new Date().toISOString(),
    updatedTime: new Date().toISOString()
  },
  {
    id: '2',
    name: 'Table 2',
    description: 'Cozy corner table',
    displayOrder: 2,
    isActive: true,
    qrCodeUrl: 'https://via.placeholder.com/200x200/667eea/ffffff?text=Table+2',
    createdTime: new Date().toISOString(),
    updatedTime: new Date().toISOString()
  },
  {
    id: '3',
    name: 'Table 3',
    description: 'Outdoor patio table',
    displayOrder: 3,
    isActive: true,
    qrCodeUrl: 'https://via.placeholder.com/200x200/667eea/ffffff?text=Table+3',
    createdTime: new Date().toISOString(),
    updatedTime: new Date().toISOString()
  },
  {
    id: '4',
    name: 'Table 4',
    description: 'Bar seating',
    displayOrder: 4,
    isActive: true,
    qrCodeUrl: 'https://via.placeholder.com/200x200/667eea/ffffff?text=Table+4',
    createdTime: new Date().toISOString(),
    updatedTime: new Date().toISOString()
  },
  {
    id: '5',
    name: 'Table 5',
    description: 'Community table',
    displayOrder: 5,
    isActive: true,
    qrCodeUrl: 'https://via.placeholder.com/200x200/667eea/ffffff?text=Table+5',
    createdTime: new Date().toISOString(),
    updatedTime: new Date().toISOString()
  },
  {
    id: '57',
    name: 'Table 57',
    description: 'Premium corner table with city view',
    displayOrder: 6,
    isActive: true,
    qrCodeUrl: 'https://via.placeholder.com/200x200/667eea/ffffff?text=Table+57',
    createdTime: new Date().toISOString(),
    updatedTime: new Date().toISOString()
  }
];

// Demo Orders (Sample data)
export const DEMO_ORDERS: Order[] = [
  {
    id: '1',
    customerName: 'John Doe',
    items: [
      {
        menuItem: DEMO_MENU_ITEMS[0], // Espresso
        quantity: 1,
        specialRequests: 'Extra hot'
      },
      {
        menuItem: DEMO_MENU_ITEMS[8], // Croissant
        quantity: 1
      }
    ],
    total: 5.50,
    status: 'pending',
    serviceType: 'dine-in',
    tableName: 'Table 1',
    timestamp: Date.now() - 300000, // 5 minutes ago
    shopId: '1',
    source: 'table-order'
  },
  {
    id: '2',
    customerName: 'Jane Smith',
    items: [
      {
        menuItem: DEMO_MENU_ITEMS[1], // Cappuccino
        quantity: 1,
        specialRequests: 'Light foam'
      },
      {
        menuItem: DEMO_MENU_ITEMS[14], // Caesar Salad
        quantity: 1,
        specialRequests: 'No croutons'
      }
    ],
    total: 14.00,
    status: 'preparing',
    serviceType: 'dine-in',
    tableName: 'Table 2',
    timestamp: Date.now() - 600000, // 10 minutes ago
    shopId: '1',
    source: 'client-cabinet'
  },
  {
    id: '3',
    customerName: 'Mike Johnson',
    items: [
      {
        menuItem: DEMO_MENU_ITEMS[11], // Chocolate Chip Cookie
        quantity: 2
      },
      {
        menuItem: DEMO_MENU_ITEMS[6], // Iced Coffee
        quantity: 1,
        specialRequests: 'Extra ice'
      }
    ],
    total: 9.50,
    status: 'ready',
    serviceType: 'takeout',
    timestamp: Date.now() - 900000, // 15 minutes ago
    shopId: '1',
    source: 'client-cabinet'
  }
];

// Helper functions for demo data
export const getDemoData = () => ({
  organization: DEMO_ORGANIZATION,
  cafeInfo: DEMO_CAFE_INFO,
  menuCategories: DEMO_MENU_CATEGORIES,
  menuItems: DEMO_MENU_ITEMS,
  tables: DEMO_TABLES,
  orders: DEMO_ORDERS
});

export const isDemoOrg = (orgSlug: string): boolean => {
  return orgSlug === DEMO_ORG_URL_NAME;
}; 